// ActivityLogPage — All account actions logged
// Layout:
//   - Top nav: < Back | "Activity log"
//   - Filter pills: All / Auth / Trading / Account / Security
//   - Timeline list

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Activity {
  id: string;
  type: 'auth' | 'trading' | 'account' | 'security' | 'payment';
  action: string;
  detail: string;
  ip: string;
  device: string;
  location: string;
  time: string;
  status?: 'success' | 'failed';
}

const ACTIVITIES: Activity[] = [
  { id: 'a1', type: 'trading', action: 'Bot trade closed',     detail: 'NQ short closed +$95.00 (+0.05%)',         ip: '203.0.113.42', device: 'iPhone 15 Pro',  location: 'San Francisco, US', time: '2026-07-24 11:32:14' },
  { id: 'a2', type: 'trading', action: 'Bot trade opened',     detail: 'ES long opened at 5,280.25',                ip: '203.0.113.42', device: 'iPhone 15 Pro',  location: 'San Francisco, US', time: '2026-07-24 10:15:08' },
  { id: 'a3', type: 'auth',    action: 'Login successful',    detail: 'Email + password sign-in',                   ip: '203.0.113.42', device: 'iPhone 15 Pro',  location: 'San Francisco, US', time: '2026-07-24 09:45:22' },
  { id: 'a4', type: 'trading', action: 'Bot strategy changed', detail: 'Breakout → Mean Reversion (locked)',          ip: '203.0.113.42', device: 'iPhone 15 Pro',  location: 'San Francisco, US', time: '2026-07-24 09:32:00' },
  { id: 'a5', type: 'security', action: 'New session',         detail: 'iPhone 15 Pro · Safari 17',                  ip: '203.0.113.42', device: 'iPhone 15 Pro',  location: 'San Francisco, US', time: '2026-07-24 09:31:15' },
  { id: 'a6', type: 'trading', action: 'Daily loss alert',     detail: 'Daily loss at 30% of limit',                  ip: '203.0.113.42', device: 'iPhone 15 Pro',  location: 'San Francisco, US', time: '2026-07-24 09:12:00' },
  { id: 'a7', type: 'trading', action: 'Trade closed',         detail: 'CL long closed -$30.00 (-0.38%)',             ip: '203.0.113.42', device: 'iPhone 15 Pro',  location: 'San Francisco, US', time: '2026-07-24 08:30:00' },
  { id: 'a8', type: 'payment', action: 'Challenge activated',  detail: 'Beginner $50K on Deepchart® · $79.00',       ip: '203.0.113.42', device: 'iPhone 15 Pro',  location: 'San Francisco, US', time: '2026-07-04 11:00:22' },
  { id: 'a9', type: 'account', action: 'Tier upgraded',        detail: 'None → Beginner',                            ip: '203.0.113.42', device: 'iPhone 15 Pro',  location: 'San Francisco, US', time: '2026-07-04 10:55:00' },
  { id: 'a10', type: 'auth',  action: 'Account created',     detail: 'Email signup · demo@funderpro.live',          ip: '198.51.100.7', device: 'iPhone 15 Pro',  location: 'San Francisco, US', time: '2026-07-04 10:50:14' },
];

const TYPE_META: Record<Activity['type'], { icon: string; color: string; label: string }> = {
  auth:     { icon: '🔐', color: '#007AFF', label: 'Auth' },
  trading:  { icon: '📈', color: '#34C759', label: 'Trading' },
  account:  { icon: '👤', color: '#5856D6', label: 'Account' },
  security: { icon: '🛡️', color: '#FF9500', label: 'Security' },
  payment:  { icon: '💳', color: '#FF3B30', label: 'Payment' },
};

const TABS = ['All', 'Auth', 'Trading', 'Account', 'Security'] as const;
type Tab = typeof TABS[number];

export function ActivityLogPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState<Tab>('All');
  const filtered = ACTIVITIES.filter((a) => tab === 'All' || a.type === tab.toLowerCase());

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Activity log</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 4v12M5 12l7 7 7-7M4 20h16" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      {/* Filter pills */}
      <div className="px-4 pt-3 flex items-center gap-2 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`activity-tab-${t}`}
            className={['shrink-0 rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition',
              tab === t ? 'bg-tf-text-1 text-white' : 'bg-white border border-tf-border text-tf-text-1'].join(' ')}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Activity list */}
      <div className="mx-4 mt-3 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-[14px] text-tf-text-3">No activity in this category</div>
        ) : (
          filtered.map((a) => {
            const meta = TYPE_META[a.type];
            return (
              <div key={a.id} data-testid={`activity-${a.id}`} className="px-4 py-3 border-b border-tf-border last:border-b-0">
                <div className="flex gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-[16px]" style={{ backgroundColor: meta.color + '18' }}>
                    {meta.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[14px] font-semibold text-tf-text-1">{a.action}</div>
                      <div className="shrink-0 text-[11px] text-tf-text-3 tabular-nums">{a.time.split(' ')[1]}</div>
                    </div>
                    <div className="text-[12px] text-tf-text-3">{a.detail}</div>
                    <div className="mt-1 flex items-center gap-1 text-[10px] text-tf-text-3">
                      <span>📍 {a.location}</span>
                      <span>·</span>
                      <span>📱 {a.device}</span>
                      <span>·</span>
                      <span className="font-mono">{a.ip}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <p className="mt-3 text-center text-[11px] text-tf-text-3 px-4">
        Showing {filtered.length} of {ACTIVITIES.length} events. Older events are available in the export.
      </p>
    </div>
  );
}
